import React, { useContext, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { ThemeContext } from '../ThemeContext/ThemeContext';
import http from '../../services/http';
import { toast } from 'react-toastify';

const TicketStatusConfig = () => {
  const { theme } = useContext(ThemeContext);
  const [stages, setStages] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    name: '',
    stage_id: '',
    is_internal: true,
    is_public: true,
    requires_justification: false,
    order: 0,
  });

  const loadStages = async () => {
    try {
      const { data } = await http.get('/ticket-stages');
      setStages(data || []);
    } catch (e) {
      toast.error('Erro ao carregar etapas.');
    }
  };

  const loadStatuses = async () => {
    try {
      const { data } = await http.get('/ticket-statuses');
      setStatuses(data || []);
    } catch (e) {
      toast.error('Erro ao carregar status.');
    }
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      await loadStages();
      await loadStatuses();
      setLoading(false);
    })();
  }, []);

  const openCreate = () => {
    setEditingId(null);
    setForm({
      name: '',
      stage_id: stages[0]?.id || '',
      is_internal: true,
      is_public: true,
      requires_justification: false,
      order: statuses.length,
    });
    setModalOpen(true);
  };

  const openEdit = (s) => {
    setEditingId(s.id);
    setForm({
      name: s.name,
      stage_id: s.stage_id,
      is_internal: s.is_internal !== false,
      is_public: s.is_public !== false,
      requires_justification: s.requires_justification === true,
      order: s.order ?? 0,
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.name?.trim()) {
      toast.warn('Informe o nome do status.');
      return;
    }
    if (!form.stage_id) {
      toast.warn('Selecione a etapa.');
      return;
    }
    try {
      if (editingId) {
        await http.put(`/ticket-statuses/${editingId}`, {
          name: form.name.trim(),
          stage_id: form.stage_id,
          is_internal: form.is_internal,
          is_public: form.is_public,
          requires_justification: form.requires_justification,
          order: form.order,
        });
        toast.success('Status atualizado.');
      } else {
        await http.post('/ticket-statuses', {
          name: form.name.trim(),
          stage_id: form.stage_id,
          is_internal: form.is_internal,
          is_public: form.is_public,
          requires_justification: form.requires_justification,
          order: form.order,
        });
        toast.success('Status criado.');
      }
      setModalOpen(false);
      await loadStatuses();
    } catch (e) {
      const msg = e.response?.data?.message || 'Erro ao salvar status.';
      toast.error(msg);
    }
  };

  const handleDelete = async (id, immutable) => {
    if (immutable) {
      toast.info('Este status é imutável e não pode ser excluído.');
      return;
    }
    if (!window.confirm('Excluir este status?')) return;
    try {
      await http.delete(`/ticket-statuses/${id}`);
      toast.success('Status excluído.');
      await loadStatuses();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Erro ao excluir.');
    }
  };

  if (loading) {
    return <Typography color="text.secondary">Carregando...</Typography>;
  }

  return (
    <Card elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}>
      <CardContent sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: theme.palette.text.primary }}>
          Status de Tickets
        </Typography>
        <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 2 }}>
          Etapas padrão: Aguardando, Cancelado, Em atendimento, Fechado, Novo, Resolvido. Crie status personalizados e vincule a uma etapa. Status padrões (Aberto, Resolvido, Cancelado) são imutáveis.
        </Typography>
        <Button variant="contained" onClick={openCreate} sx={{ textTransform: 'none', mb: 2 }}>
          Novo status
        </Button>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {statuses.map((s) => (
            <Card key={s.id} variant="outlined" sx={{ minWidth: 200, borderRadius: 2 }}>
              <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={600}>
                      {s.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Etapa: {s.stage?.name ?? '-'}
                    </Typography>
                    <Box sx={{ mt: 0.5 }}>
                      {s.is_immutable && <Chip size="small" label="Imutável" sx={{ mr: 0.5 }} />}
                      {s.requires_justification && <Chip size="small" label="Exige justificativa" color="primary" />}
                    </Box>
                  </Box>
                  <Box>
                    <Button size="small" onClick={() => openEdit(s)} sx={{ textTransform: 'none', mr: 0.5 }}>
                      Editar
                    </Button>
                    <Button size="small" color="error" onClick={() => handleDelete(s.id, s.is_immutable)} sx={{ textTransform: 'none' }} disabled={!!s.is_immutable}>
                      Excluir
                    </Button>
                  </Box>
                </Box>
                <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                  {s.is_internal && 'Interno '}
                  {s.is_public && 'Público'}
                </Typography>
              </CardContent>
            </Card>
          ))}
        </Box>

        <Dialog open={modalOpen} onClose={() => setModalOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>{editingId ? 'Editar status' : 'Novo status'}</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              label="Nome"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              sx={{ mt: 1, mb: 2 }}
              disabled={!!editingId && statuses.find(s => s.id === editingId)?.is_immutable}
            />
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Etapa</Typography>
            <Select
              fullWidth
              value={form.stage_id || ''}
              onChange={(e) => setForm({ ...form, stage_id: e.target.value })}
              displayEmpty
              sx={{ mb: 2 }}
            >
              {stages.map((st) => (
                <MenuItem key={st.id} value={st.id}>{st.name}</MenuItem>
              ))}
            </Select>
            <FormControlLabel
              control={<Switch checked={form.is_internal} onChange={(e) => setForm({ ...form, is_internal: e.target.checked })} />}
              label="Para tickets internos"
            />
            <br />
            <FormControlLabel
              control={<Switch checked={form.is_public} onChange={(e) => setForm({ ...form, is_public: e.target.checked })} />}
              label="Para tickets públicos"
            />
            <br />
            <FormControlLabel
              control={<Switch checked={form.requires_justification} onChange={(e) => setForm({ ...form, requires_justification: e.target.checked })} />}
              label="Exige justificativa (substatus)"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setModalOpen(false)}>Cancelar</Button>
            <Button variant="contained" onClick={handleSave}>Salvar</Button>
          </DialogActions>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default TicketStatusConfig;
